/**
 * <hw-chat> Web Component.
 *
 * Owns the Shadow DOM, subscribes to a ChatController for state, and
 * re-renders on every state change. Event delegation (data-action attrs)
 * keeps handler plumbing simple — one click / keydown / drag listener
 * at the root, dispatching by action string.
 */
import type { AuthProvider, ChatConfig, HwChatOptions } from '../core/types.js';
import { ChatController, formatBytes, type Attachment, type ChatState, type Msg } from '../core/chat-controller.js';
import { styles } from './styles.js';
import { renderMarkdown } from './markdown.js';
import {
  startVoiceSession,
  type VoiceMode,
  type VoiceProvider,
  type VoiceSessionHandle,
  type VoiceTurn,
} from '../core/voice-driver.js';
import { fetchVoiceTicket } from '../core/api.js';

const TAG = 'hw-chat';
const DEFAULT_API_BASE = 'https://api.hostwebhook.com';

// Conditional base class so this module loads cleanly in SSR contexts
// (Next.js build workers, Astro / Nuxt / Remix prerender). The real
// HTMLElement only exists in the browser; Node throws "HTMLElement is
// not defined" before a framework can mark the importer client-only.
// In Node the class is defined against a stub and never instantiated
// because registerHwChat() returns early below.
const BaseElement: typeof HTMLElement =
  typeof HTMLElement !== 'undefined'
    ? HTMLElement
    : (class {} as unknown as typeof HTMLElement);

export class HwChatElement extends BaseElement {
  /** Programmatic auth provider. The React wrapper assigns this in a
   *  useEffect, which fires AFTER `connectedCallback` already ran `boot()`,
   *  so the setter has to forward late assignments to the live controller —
   *  otherwise the AuthManager keeps the null snapshot it captured at boot
   *  and every signed request goes out unauthenticated (401 from ingress). */
  private _authProvider: AuthProvider | null = null;
  get authProvider(): AuthProvider | null { return this._authProvider; }
  set authProvider(provider: AuthProvider | null) {
    this._authProvider = provider;
    this.controller?.setAuthProvider(provider);
  }

  private root: ShadowRoot;
  private controller: ChatController | null = null;
  private unsubscribe: (() => void) | null = null;
  private dragCounter = 0;
  private initialized = false;
  private systemDarkMq: MediaQueryList | null = null;
  private systemDarkListener: ((e: MediaQueryListEvent) => void) | null = null;

  // The textarea is kept alive across renders. innerHTML replacement
  // destroys all children, which kills focus + selection no matter what you
  // do afterwards, so we detach the textarea *before* the swap and slot it
  // back in where the new HTML left a placeholder. Node identity is
  // preserved → focus is preserved natively by the browser.
  private textareaEl: HTMLTextAreaElement | null = null;
  private rootListenersAttached = false;

  static get observedAttributes(): string[] {
    return [
      'chat-id', 'api-base', 'auth-endpoint', 'theme', 'primary-color',
      'preview-token', 'embedded',
      'voice-agent-id', 'voice-provider', 'voice-sdk-url',
    ];
  }

  /** Active voice session, if any. Mutually exclusive with text-mode
   *  streaming — the SDK takes over audio capture while it lives. */
  private voiceSession: VoiceSessionHandle | null = null;
  private voiceMode: VoiceMode = 'idle';

  private postMessageListener: ((e: MessageEvent) => void) | null = null;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    if (this.initialized) return;
    this.initialized = true;
    void this.boot();
  }

  disconnectedCallback(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    // Tear down voice session before the element vanishes — leaking it
    // keeps the mic on after the widget unmounts (visible bug on SPAs
    // that route between pages while a call is active).
    void this.endVoice('element_disconnected');
    if (this.systemDarkMq && this.systemDarkListener) {
      this.systemDarkMq.removeEventListener('change', this.systemDarkListener);
    }
    if (this.postMessageListener) {
      window.removeEventListener('message', this.postMessageListener);
      this.postMessageListener = null;
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (!this.initialized) return;
    if (oldValue === newValue) return;
    if (name === 'chat-id') {
      this.initialized = false;
      this.controller?.stop();
      this.unsubscribe?.();
      this.connectedCallback();
    }
    if (name === 'theme' || name === 'primary-color') {
      this.applyThemeVars();
    }
    if (name === 'preview-token') {
      this.controller?.setPreviewToken(newValue);
    }
    if (name === 'embedded') {
      // Header close button only renders when this flag flips — re-render
      // against current state.
      if (this.controller) this.render(this.controller.state);
    }
  }

  private readOptions(): Required<Pick<HwChatOptions, 'chatId' | 'apiBase'>> & HwChatOptions {
    return {
      chatId: this.getAttribute('chat-id') ?? '',
      apiBase: this.getAttribute('api-base') ?? DEFAULT_API_BASE,
      authEndpoint: this.getAttribute('auth-endpoint') ?? undefined,
      theme: (this.getAttribute('theme') as HwChatOptions['theme']) ?? undefined,
      primaryColor: this.getAttribute('primary-color') ?? undefined,
      authProvider: this.authProvider ?? undefined,
      voiceAgentId: this.getAttribute('voice-agent-id') ?? undefined,
      voiceProvider: (this.getAttribute('voice-provider') as VoiceProvider | null) ?? undefined,
      voiceSdkUrl: this.getAttribute('voice-sdk-url') ?? undefined,
    };
  }

  private async boot(): Promise<void> {
    const opts = this.readOptions();
    if (!opts.chatId) {
      this.renderBootError('hw-chat is missing a chat-id attribute');
      return;
    }
    this.controller = new ChatController({
      chatId: opts.chatId,
      apiBase: opts.apiBase,
      authProvider: opts.authProvider ?? null,
      authEndpoint: opts.authEndpoint ?? null,
      themeOverride: opts.theme,
      primaryColorOverride: opts.primaryColor,
      previewToken: this.getAttribute('preview-token'),
    });
    this.unsubscribe = this.controller.subscribe((state) => this.render(state));
    this.watchSystemTheme();
    this.watchParentMessages();
    this.applyThemeVars();
    this.render(this.controller.state);
    await this.controller.boot();
  }

  /** Dashboard preview iframe refreshes the short-lived preview token every
   *  25 minutes over postMessage so long sessions don't expire. Same message
   *  contract the dashboard's WidgetPreview component sends. */
  private watchParentMessages(): void {
    this.postMessageListener = (e: MessageEvent) => {
      const data = e.data as { type?: string; token?: string } | null;
      if (!data) return;
      if (data.type === 'hw-chat-preview-token' && typeof data.token === 'string') {
        this.controller?.setPreviewToken(data.token);
        this.setAttribute('preview-token', data.token);
      }
    };
    window.addEventListener('message', this.postMessageListener);
  }

  /* ── Theme resolution ──────────────────────────────────────────────── */

  private watchSystemTheme(): void {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    this.systemDarkMq = window.matchMedia('(prefers-color-scheme: dark)');
    this.systemDarkListener = () => this.applyThemeVars();
    this.systemDarkMq.addEventListener('change', this.systemDarkListener);
  }

  private applyThemeVars(): void {
    const cfg = this.controller?.state.config ?? null;
    const themeAttr = this.getAttribute('theme') as HwChatOptions['theme'] | null;
    const mode = themeAttr ?? cfg?.theme.mode ?? 'dark';
    const systemDark = this.systemDarkMq?.matches ?? true;
    const isDark = mode === 'dark' || (mode === 'auto' && systemDark);
    this.dataset.theme = isDark ? 'dark' : 'light';

    const primary = this.getAttribute('primary-color') ?? cfg?.theme.primaryColor;
    if (primary) {
      this.style.setProperty('--hw-primary', primary);
      this.style.setProperty('--hw-primary-rgb', hexToRgb(primary) ?? '124, 58, 237');
    }
  }

  /* ── Render ──────────────────────────────────────────────────────────── */

  private render(state: ChatState): void {
    if (state.bootError) {
      this.renderBootError(state.bootError);
      return;
    }
    if (!state.config) {
      this.root.innerHTML = `<style>${styles}</style><div class="shell"><div class="boot-error">Loading…</div></div>`;
      return;
    }

    // Re-apply theme vars when config arrives (the config may carry theme info).
    this.applyThemeVars();

    const cfg = state.config;
    const attachmentsEnabled = !!cfg.attachments?.enabled;
    const hasUserTurn = state.messages.some((m) => m.role === 'user');
    const embedded = this.hasAttribute('embedded');
    // Attribute wins over config so per-page overrides still work for
    // consumers that ship a custom voice agent on top of a generic
    // chat trigger. Empty attribute → fall back to whatever the
    // dashboard configured on the Chat Trigger.
    const voiceAgentId =
      this.getAttribute('voice-agent-id') ?? cfg.voiceAgentId ?? '';
    const voiceMode = this.voiceMode;
    const hasBlocking = state.attachments.some((a) => a.status !== 'clean');
    const disableSend = state.streaming || (!state.input.trim() && state.attachments.length === 0) || hasBlocking || !state.sessionId;
    const acceptMimes = (cfg.attachments?.allowedMimeTypes ?? []).join(',');

    const html = `
      <style>${styles}</style>
      <div class="shell-wrap">
        ${state.dragActive && attachmentsEnabled ? dropOverlay() : ''}
        <div class="shell">
          ${header(cfg, hasUserTurn, embedded, voiceAgentId, voiceMode)}
          <div class="messages" part="messages">
            ${state.messages.map((m, i) => messageHtml(m, i === state.messages.length - 1, state)).join('')}
            ${state.error ? `<div class="error-box">${escapeHtml(state.error)}</div>` : ''}
          </div>
          <div class="composer">
            <div class="composer-inner">
              ${state.attachments.length > 0 ? attachmentsStrip(state.attachments) : ''}
              <div class="composer-row">
                ${attachmentsEnabled ? `
                  <input type="file" data-action="pick-files" multiple accept="${escapeHtml(acceptMimes)}" style="display:none" />
                  <button class="btn-icon" data-action="open-picker" ${state.streaming || state.attachments.length >= (cfg.attachments?.maxPerMessage ?? 5) ? 'disabled' : ''} title="Attach a file" aria-label="Attach a file">
                    ${iconPaperclip()}
                  </button>
                ` : ''}
                <span data-textarea-slot></span>
                ${state.streaming
                  ? `<button class="btn-send stop" data-action="stop" title="Stop">${iconStop()}</button>`
                  : `<button class="btn-send" data-action="send" ${disableSend ? 'disabled' : ''} title="Send">${iconSend()}</button>`
                }
              </div>
            </div>
            <div class="footer">Powered by HostWebhook</div>
          </div>
        </div>
      </div>
    `;

    // Node identity alone isn't enough — removeChild fires blur
    // synchronously, so the user loses focus the moment we detach. Capture
    // focus + caret before the swap, then explicitly re-apply them after
    // the textarea is back in the tree.
    const hadFocus = !!this.textareaEl && this.root.activeElement === this.textareaEl;
    const savedSelection: [number, number] | null =
      hadFocus && this.textareaEl
        ? [this.textareaEl.selectionStart ?? 0, this.textareaEl.selectionEnd ?? 0]
        : null;

    if (this.textareaEl?.parentNode) {
      this.textareaEl.parentNode.removeChild(this.textareaEl);
    }

    this.root.innerHTML = html;

    const slot = this.root.querySelector('[data-textarea-slot]');
    if (slot) {
      if (!this.textareaEl) {
        this.textareaEl = this.createTextarea(cfg.placeholder || 'Type your message…');
      }
      // Keep disabled state current. Never overwrite the textarea's own
      // value while it has focus — that's mid-typing and would move the
      // caret out from under the user.
      this.textareaEl.disabled = state.streaming;
      if (!hadFocus && this.textareaEl.value !== state.input) {
        this.textareaEl.value = state.input;
      }
      slot.replaceWith(this.textareaEl);

      if (hadFocus) {
        this.textareaEl.focus();
        if (savedSelection) {
          try { this.textareaEl.setSelectionRange(savedSelection[0], savedSelection[1]); } catch { /* ignore */ }
        }
      }
    }

    this.wireEvents();
    this.autoresizeTextarea();
    this.scrollToBottom();
  }

  /** Build the persistent textarea once. Listeners attached here survive
   *  every subsequent render because the node itself is never destroyed. */
  private createTextarea(placeholder: string): HTMLTextAreaElement {
    const ta = document.createElement('textarea');
    ta.className = 'textarea';
    ta.setAttribute('data-role', 'input');
    ta.placeholder = placeholder;
    ta.rows = 1;
    // Belt + suspenders alongside CSS min-height: 44px / max-height: 200px
    // and the autoresize empty-short-circuit. Setting all three inline means
    // the row's box reads correctly from the very first paint — bypasses
    // any stale CSS bundle still cached in a long-lived iframe + sidesteps
    // any host-page cascade pollution that might leak through Shadow DOM
    // edge cases.
    ta.style.height = '44px';
    ta.style.minHeight = '44px';
    ta.style.maxHeight = '200px';
    const c = this.controller;
    if (!c) return ta;
    ta.addEventListener('input', () => {
      c.setInput(ta.value);
    });
    ta.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void c.sendMessage(c.state.input);
      }
    });
    return ta;
  }

  /* ── Voice ───────────────────────────────────────────────────────────── */

  private async toggleVoice(): Promise<void> {
    if (this.voiceSession) {
      await this.endVoice('user_hangup');
      return;
    }
    await this.startVoice();
  }

  private async startVoice(): Promise<void> {
    const opts = this.readOptions();
    const cfg = this.controller?.state.config;
    const controller = this.controller;
    // Attribute > config so consumers can override per-embed without
    // re-saving the trigger. Same precedence as the render path.
    const voiceAgentId = opts.voiceAgentId ?? cfg?.voiceAgentId ?? '';
    // Cast through unknown because cfg.voiceProvider is typed with the
    // empty-string fallback the API uses to mean "not configured", but
    // the SDK enum doesn't carry that case.
    const cfgProvider = cfg?.voiceProvider as string | undefined;
    const voiceProvider =
      opts.voiceProvider ??
      (cfgProvider && cfgProvider !== '' ? (cfgProvider as VoiceProvider) : undefined) ??
      'elevenlabs';
    if (!voiceAgentId) return;
    if (this.voiceSession) return;
    if (!controller) return;

    const sessionId = controller.state.sessionId;
    if (!sessionId) {
      this.controller?.appendMessage(
        'assistant',
        '⚠ Could not start voice call: chat session not ready yet. Send a message first or refresh.',
      );
      return;
    }

    this.setVoiceMode('connecting');
    try {
      // Mint the ticket via HW backend first. ElevenLabs ignores
      // `connectionType: 'websocket'` for public agents (only agentId)
      // and forces WebRTC/LiveKit, which times out against their older
      // LiveKit cluster. Handing the SDK a signedUrl skips LiveKit
      // entirely. Same path the dashboard's Test Agent + voice-widget
      // <hw-voice> already use.
      const ticket = await fetchVoiceTicket(
        opts.apiBase,
        opts.chatId,
        sessionId,
        controller.authManager,
      );

      // Provider mismatch shouldn't happen (backend reads voiceProvider
      // off the same trigger config) but if it does, the discriminated
      // union below would crash silently — fail loud instead.
      if (ticket.provider !== voiceProvider) {
        throw new Error(
          `Voice provider mismatch — trigger says ${voiceProvider} but backend minted ${ticket.provider}.`,
        );
      }

      this.voiceSession = await startVoiceSession({
        provider: voiceProvider,
        agentId: ticket.agentId,
        signedUrl: ticket.provider === 'elevenlabs' ? ticket.signedUrl : undefined,
        sdkUrl: opts.voiceSdkUrl,
        onTurn: (turn: VoiceTurn) => {
          // Pipe transcript turns into the message list so users see
          // the conversation history alongside text-mode turns.
          this.controller?.appendMessage(
            turn.role === 'user' ? 'user' : 'assistant',
            turn.text,
          );
        },
        onMode: (mode) => this.setVoiceMode(mode),
        onEnd: () => {
          this.voiceSession = null;
          this.setVoiceMode('idle');
        },
        onError: (err) => {
          // Surface as a one-off "system" assistant message so the
          // visitor sees what went wrong without us bolting a toast
          // onto the widget. Error text is short by design.
          this.controller?.appendMessage('assistant', `⚠ Voice error: ${err.message}`);
          this.voiceSession = null;
          this.setVoiceMode('idle');
        },
      });
    } catch (err) {
      this.controller?.appendMessage(
        'assistant',
        `⚠ Could not start voice call: ${(err as Error).message}`,
      );
      this.voiceSession = null;
      this.setVoiceMode('idle');
    }
  }

  private async endVoice(_reason: string): Promise<void> {
    const session = this.voiceSession;
    this.voiceSession = null;
    this.setVoiceMode('idle');
    try {
      await session?.end();
    } catch {
      // SDK may already have torn down — silent failure is fine.
    }
  }

  private setVoiceMode(mode: VoiceMode): void {
    if (this.voiceMode === mode) return;
    this.voiceMode = mode;
    if (this.controller) this.render(this.controller.state);
  }

  private renderBootError(msg: string): void {
    this.root.innerHTML = `
      <style>${styles}</style>
      <div class="shell-wrap"><div class="shell"><div class="boot-error">
        <strong>Chat unavailable</strong>${escapeHtml(msg)}
      </div></div></div>
    `;
  }

  /* ── Event wiring ─────────────────────────────────────────────────────── */

  private wireEvents(): void {
    const c = this.controller;
    if (!c) return;

    // Root-level listeners attach exactly once. Before this guard, every
    // render appended another click listener and we'd balloon to hundreds
    // after a chatty session.
    this.wireRootListenersOnce();

    const fileInput = this.root.querySelector('[data-action="pick-files"]') as HTMLInputElement | null;
    if (fileInput) {
      fileInput.addEventListener('change', (e) => {
        const files = (e.target as HTMLInputElement).files;
        if (files) c.addFiles(files);
        (e.target as HTMLInputElement).value = '';
      });
    }
  }

  private wireRootListenersOnce(): void {
    if (this.rootListenersAttached) return;
    this.rootListenersAttached = true;
    const c = this.controller;
    if (!c) return;

    this.root.addEventListener('click', (e) => {
      const target = (e.target as HTMLElement).closest('[data-action]');
      if (!target) return;
      const action = target.getAttribute('data-action');
      switch (action) {
        case 'send': {
          void c.sendMessage(c.state.input);
          break;
        }
        case 'stop': c.stop(); break;
        case 'reset': {
          if (confirm('Start a fresh chat? Current conversation will be cleared.')) c.reset();
          break;
        }
        case 'open-picker': {
          const input = this.root.querySelector('[data-action="pick-files"]') as HTMLInputElement | null;
          input?.click();
          break;
        }
        case 'close-embed': {
          try { window.parent.postMessage({ type: 'hw-chat-close' }, '*'); } catch { /* sandboxed */ }
          break;
        }
        case 'remove-attachment': {
          const id = target.getAttribute('data-client-id');
          if (id) c.removeAttachment(id);
          break;
        }
        case 'toggle-voice': {
          void this.toggleVoice();
          break;
        }
      }
    });

    // Textarea listeners live on the persistent textarea node itself —
    // see createTextarea(). Not attached here because wireEvents runs per
    // render and we'd duplicate.

    // Drag / drop — attach on host element so overlay sees events regardless of nested child.
    this.addEventListener('dragenter', (e) => {
      if (!c.state.config?.attachments?.enabled) return;
      e.preventDefault();
      this.dragCounter++;
      if (e.dataTransfer?.types.includes('Files')) c.setDragActive(true);
    });
    this.addEventListener('dragleave', () => {
      this.dragCounter = Math.max(0, this.dragCounter - 1);
      if (this.dragCounter === 0) c.setDragActive(false);
    });
    this.addEventListener('dragover', (e) => {
      if (c.state.config?.attachments?.enabled) e.preventDefault();
    });
    this.addEventListener('drop', (e) => {
      if (!c.state.config?.attachments?.enabled) return;
      e.preventDefault();
      this.dragCounter = 0;
      c.setDragActive(false);
      if (e.dataTransfer?.files?.length) c.addFiles(e.dataTransfer.files);
    });
  }

  private autoresizeTextarea(): void {
    const ta = this.root.querySelector('[data-role="input"]') as HTMLTextAreaElement | null;
    if (!ta) return;
    // Empty textarea: always 44px (one line + padding, matches the buttons).
    // Skip the scrollHeight dance entirely — on a freshly-mounted iframe the
    // first layout pass can report stale scrollHeight values (seen as high
    // as the parent container's height), which then cap at 200px and ruin
    // the composer. Short-circuiting on empty avoids the trap completely.
    if (!ta.value) {
      ta.style.height = '44px';
      return;
    }
    ta.style.height = 'auto';
    ta.style.height = Math.min(Math.max(ta.scrollHeight, 44), 200) + 'px';
  }

  private scrollToBottom(): void {
    const msgs = this.root.querySelector('.messages');
    if (!msgs) return;
    msgs.scrollTop = msgs.scrollHeight;
  }
}

/* ── Templates ───────────────────────────────────────────────────────────── */

function header(
  cfg: ChatConfig,
  hasUserTurn: boolean,
  embedded: boolean,
  voiceAgentId: string,
  voiceMode: VoiceMode,
): string {
  const avatar = cfg.theme.avatarUrl;
  const resetLabel = embedded ? 'Reset' : 'New chat';
  const voiceActive = voiceMode !== 'idle' && voiceMode !== 'ended';
  // Three states for the mic button: idle (start), connecting/listening
  // (pulsing), speaking (filled). The data-attr drives the CSS so the
  // shadow-DOM stylesheet can react without us re-rendering the SVG.
  const voiceTitle = voiceActive ? 'End voice call' : 'Start voice call';
  const voiceLabel = (() => {
    switch (voiceMode) {
      case 'connecting': return 'Connecting…';
      case 'listening':  return 'Listening';
      case 'speaking':   return 'Speaking';
      default:           return '';
    }
  })();
  return `
    <div class="header">
      <div class="avatar">
        ${avatar ? `<img src="${escapeHtml(avatar)}" alt="" />` : iconChat()}
      </div>
      <div class="header-text">
        <div class="title">${escapeHtml(cfg.title || 'Chat')}</div>
        ${voiceLabel
          ? `<div class="subtitle voice-status">${escapeHtml(voiceLabel)}</div>`
          : cfg.subtitle ? `<div class="subtitle">${escapeHtml(cfg.subtitle)}</div>` : ''}
      </div>
      ${voiceAgentId ? `<button class="header-btn voice-btn" data-action="toggle-voice" data-voice-mode="${voiceMode}" title="${voiceTitle}" aria-label="${voiceTitle}">${iconMic(voiceActive)}</button>` : ''}
      ${hasUserTurn ? `<button class="header-btn" data-action="reset" title="Start a new conversation">${escapeHtml(resetLabel)}</button>` : ''}
      ${embedded ? `<button class="header-btn" data-action="close-embed" title="Close" aria-label="Close">${iconXSmall()}</button>` : ''}
    </div>
  `;
}

function messageHtml(m: Msg, isLast: boolean, state: ChatState): string {
  if (m.role === 'user') {
    // Attachments + bubble are direct flex children of .msg-row.user (column
    // flex, right-aligned in CSS). The previous nested wrapper had its own
    // max-width: 80% AND the bubble had max-width: 80% — browsers resolve
    // nested percentage widths in shrink-to-fit containers to degenerate
    // values, so "hola" wrapped as "h / ol / a" and longer text broke at
    // arbitrary points with the bubble stretching full-width.
    return `
      <div class="msg">
        <div class="msg-row user">
          ${m.attachments && m.attachments.length > 0 ? `
            <div class="msg-attachments">
              ${m.attachments.map((a) => `
                <span class="msg-attach-chip" title="${escapeHtml(a.originalName)} · ${formatBytes(a.size)}">
                  ${iconPaperclip()}<span class="name">${escapeHtml(a.originalName)}</span>
                </span>
              `).join('')}
            </div>
          ` : ''}
          ${m.content ? `<div class="bubble-user">${escapeHtml(m.content)}</div>` : ''}
        </div>
      </div>
    `;
  }

  const showTyping = isLast && state.streaming && !state.activeTool && !m.content;
  const showCursor = isLast && state.streaming && !state.activeTool && m.content;
  return `
    <div class="msg">
      ${m.toolCalls && m.toolCalls.length > 0 ? `
        <div class="tool-calls">
          ${m.toolCalls.map((c) => `
            <span class="tool-pill ${c.running ? 'running' : 'done'}">
              ${c.running ? iconSpinner() : iconCheck()}
              <span>${escapeHtml(c.name)}</span>
            </span>
          `).join('')}
        </div>
      ` : ''}
      ${m.content ? `<div class="assistant">${renderAssistantContent(m.content)}${showCursor ? '<span class="cursor"></span>' : ''}</div>` : ''}
      ${showTyping ? `<div class="typing"><span></span><span></span><span></span></div>` : ''}
    </div>
  `;
}

function attachmentsStrip(list: Attachment[]): string {
  return `
    <div class="attachments-strip">
      ${list.map((a) => {
        const isBad = a.status === 'infected' || a.status === 'scan_failed' || a.status === 'error';
        const isWorking = a.status === 'uploading' || a.status === 'scanning';
        const klass = isBad ? 'bad' : a.status === 'clean' ? 'ok' : 'neutral';
        const label =
          a.status === 'uploading' ? `Uploading ${a.progress}%`
          : a.status === 'scanning' ? 'Scanning…'
          : a.status === 'clean' ? 'Ready'
          : a.status === 'infected' ? (a.error ? `Blocked: ${a.error}` : 'Blocked')
          : a.status === 'scan_failed' ? (a.error ? `Scan failed: ${a.error}` : 'Scan failed')
          : (a.error ? `Upload failed: ${a.error}` : 'Upload failed');
        const titleAttr = a.error ? `${a.file.name}: ${a.error}` : `${a.file.name} · ${formatBytes(a.file.size)} · ${label}`;
        return `
          <span class="attach-chip ${klass}" title="${escapeHtml(titleAttr)}">
            ${isWorking ? iconSpinner() : a.status === 'clean' ? iconCheck() : iconX()}
            <span class="name">${escapeHtml(a.file.name)}</span>
            <span class="status">· ${escapeHtml(label)}</span>
            <button type="button" class="remove" data-action="remove-attachment" data-client-id="${escapeHtml(a.clientId)}" aria-label="Remove ${escapeHtml(a.file.name)}">
              ${iconXSmall()}
            </button>
          </span>
        `;
      }).join('')}
    </div>
  `;
}

function dropOverlay(): string {
  return `
    <div class="drop-overlay">
      <div class="drop-text">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
        </svg>
        <p>Drop to attach</p>
      </div>
    </div>
  `;
}

/* ── Icons ───────────────────────────────────────────────────────────────── */

function iconChat(): string {
  return `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" /></svg>`;
}
function iconPaperclip(): string {
  return `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 0 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>`;
}
function iconSend(): string {
  return `<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" /></svg>`;
}
function iconStop(): string { return `<svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>`; }
function iconSpinner(): string { return `<svg class="spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.25" stroke-width="4"/><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>`; }
function iconCheck(): string { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>`; }
function iconX(): string { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>`; }
function iconXSmall(): string { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="width:10px;height:10px;"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>`; }

function iconMic(active: boolean): string {
  // Active state shows a filled mic with a pulse ring; idle is the
  // outline. The SVG fill switches based on `active` so the same
  // markup serves both — CSS handles the pulse animation off the
  // data-voice-mode attribute on the button.
  if (active) {
    return `<svg viewBox="0 0 24 24" fill="currentColor" style="width:14px;height:14px;"><path d="M12 14a3 3 0 003-3V5a3 3 0 10-6 0v6a3 3 0 003 3zm5-3a5 5 0 01-10 0H5a7 7 0 006 6.93V21h2v-3.07A7 7 0 0019 11z"/></svg>`;
  }
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:14px;height:14px;"><path stroke-linecap="round" stroke-linejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"/></svg>`;
}

/* ── Helpers ────────────────────────────────────────────────────────────── */

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function renderAssistantContent(content: string): string {
  return renderMarkdown(content);
}

function hexToRgb(hex: string): string | null {
  const h = hex.replace('#', '').trim();
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return `${(n >> 16) & 0xff}, ${(n >> 8) & 0xff}, ${n & 0xff}`;
}

/** Register once. Calling define twice throws, so guard first. */
export function registerHwChat(): void {
  if (typeof customElements === 'undefined') return;
  if (customElements.get(TAG)) return;
  customElements.define(TAG, HwChatElement);
}

registerHwChat();
